import { lazy } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';
import { fleetSections } from './sections';
import type { FleetSectionDef } from './pages/FleetSectionPage';

// Pages and tabs load when first opened (NFR-04, docs §169).
const FleetHomePage = lazy(() => import('./pages/FleetHomePage'));
const FleetSectionPage = lazy(() => import('./pages/FleetSectionPage'));
const VehiclePage = lazy(() => import('./pages/VehiclePage'));
const JobCardPage = lazy(() => import('./pages/JobCardPage'));

/**
 * The fleet module's routes, spread into the signed-in layout. Every page sits behind `RequireModule`,
 * so an organisation without the module sees the "not enabled" card rather than pages meeting 403s.
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Fleet.View}>
    <RequireModule module={Modules.Fleet}>{element}</RequireModule>
  </RequirePermission>
);

const section = <K extends string>(def: FleetSectionDef<K>, path: string): RouteObject =>
  ({ path, element: guard(<FleetSectionPage section={def as unknown as FleetSectionDef} />) });

export const fleetRoutes: RouteObject[] = [
  { path: 'fleet', element: guard(<FleetHomePage />) },
  section(fleetSections.vehicles, 'fleet/vehicles/:view?'),
  { path: 'fleet/vehicle/:id', element: guard(<VehiclePage />) },
  section(fleetSections.acquisition, 'fleet/acquisition/:view?'),
  section(fleetSections.drivers, 'fleet/drivers/:view?'),
  section(fleetSections.trips, 'fleet/trips/:view?'),
  section(fleetSections.fuel, 'fleet/fuel/:view?'),
  section(fleetSections.workshop, 'fleet/workshop/:view?'),
  { path: 'fleet/job-card/:id', element: guard(<JobCardPage />) },
  section(fleetSections.costs, 'fleet/costs/:view?'),
  section(fleetSections.inspections, 'fleet/inspections/:view?'),
  section(fleetSections.incidents, 'fleet/incidents/:view?'),
  section(fleetSections.usage, 'fleet/usage/:view?'),
  section(fleetSections.planning, 'fleet/planning/:view?'),
  section(fleetSections.exceptions, 'fleet/exceptions/:view?'),
  section(fleetSections.settings, 'fleet/settings/:view?'),
];
